/* eslint-disable security/detect-object-injection */
import styled, { css, keyframes } from "styled-components";
import { Paragraph } from "../../../Typography";
import type { BetRaceStatusType } from "../../types";

type ColorsMapper = {
  [RaceStatus in BetRaceStatusType]: string;
};

const defaultRaceStatusColors: ColorsMapper = {
  open: "--fd-colors-system-neutral-background-default",
  raceOff: "--fd-colors-system-neutral-background-default",
  win: "--fd-colors-system-positive-background-default",
  lost: "--fd-colors-system-important-background-default",
  attention: "--fd-colors-system-warning-background-default",
  unknown: "--fd-colors-system-neutral-background-default"
};

const legTextColors: ColorsMapper = {
  open: "--fd-colors-system-neutral-content-default",
  raceOff: "--fd-colors-system-neutral-content-subtle",
  win: "--fd-colors-system-positive-content-accent",
  lost: "--fd-colors-system-important-content-accent",
  attention: "--fd-colors-system-warning-content-accent",
  unknown: "--fd-colors-system-neutral-content-accent"
};

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  
  100% {
    opacity: 1;
  }
`;

export const Content = styled.div`
  justify-content: center;
  flex-shrink: 0;
`;

export const PositionsContainer = styled.div<{
  raceStatus?: BetRaceStatusType;
  isLeg: boolean | undefined;
}>`
  &:not(:last-child) {
    padding-bottom: 8px;
  }

  ${({ raceStatus, isLeg }) =>
    isLeg &&
    css`
      position: relative;

      &:not(:first-child) {
        margin-top: 2px;
      }

      &:not(:last-child) {
        &::after {
          content: "";
          width: 2px;
          height: calc(100% - 18px);
          ${raceStatus &&
          css`
            background: var(${defaultRaceStatusColors[raceStatus]});
          `}
          border-radius: 1px;
          position: absolute;
          top: 20px;
          left: 9px;
          z-index: 2;
        }
      }
    `}
`;

export const RowWrapper = styled.div<{
  isLeg: boolean | undefined;
}>`
  ${({ isLeg }) =>
    isLeg
      ? css`
          display: flex;
          position: relative;
        `
      : css`
          display: grid;
          grid-template-columns: 25px auto;
          gap: 12px 8px;
          position: relative;
          width: 100%;
        `}
`;

export const DetailAction = styled.div`
  position: absolute;
  width: 100%;
  height: 20px;
  z-index: 3;
`;

export const PickBetLabelIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: 20px;
  width: 20px;
  margin-right: 8px;
  position: relative;
  z-index: 2;
`;

export const LabelIconWrapper = styled.div<{
  raceStatus?: BetRaceStatusType;
}>`
  position: relative;
  width: 16px;
  height: 16px;
  ${({ raceStatus }) => {
    if (raceStatus && ["raceOff"].includes(raceStatus)) {
      return css`
        border: 2px solid var(--fd-colors-content-subtle);
        background: var(--fd-colors-background-surface);
        border-radius: 50%;
      `;
    }

    return "";
  }}
`;

export const LabelDot = styled.div<{
  raceStatus?: BetRaceStatusType;
}>`
  position: absolute;
  content: "";
  width: 4px;
  height: 4px;
  top: calc(50% - 2px);
  left: calc(50% - 2px);
  border-radius: 50%;
  background: var(--fd-colors-content-subtle);
`;

export const SaddleContainer = styled.div<{
  isLeg: boolean | undefined;
  hasDetails: boolean;
  hasAlternate?: boolean;
}>`
  width: ${({ isLeg, hasDetails }) =>
    isLeg && !hasDetails ? "fit-content" : "100%"};
  ${({ hasDetails, isLeg }) =>
    isLeg &&
    hasDetails &&
    css`
      margin-top: 8px;
    `}
`;

export const LegParagraph = styled(Paragraph)<{
  raceStatus?: BetRaceStatusType;
}>`
  color: ${({ raceStatus }) =>
    raceStatus
      ? `var(${legTextColors[raceStatus]})`
      : "var(--fd-colors-content-subtle)"};
`;

export const DetailsHr = styled.hr.attrs({
  "data-qa-label": "myBets-horizontalRule"
})`
  height: 1px;
  background-color: var(--fd-colors-border-subtle);
  width: calc(100% - 4px);
  border: none;
  position: relative;
  left: 2px;
  z-index: 2;
  margin: 8px 0 0;
  animation: ${fadeIn} 0.2s cubic-bezier(0.39, 0.575, 0.565, 1) both;
`;

export const TextAndSelectionContainer = styled.div<{
  hasDetails: boolean;
  isLeg: boolean | undefined;
}>`
  display: flex;
  font-family: "Roboto-Regular", sans-serif;
  flex-direction: ${({ hasDetails }) => (hasDetails ? "column" : "row")};
  ${({ isLeg }) =>
    isLeg &&
    css`
      width: 100%;
    `};
`;

export const WillPaysInfoContainer = styled.div`
  display: flex;
  width: 100%;
  margin-left: 2px;
  margin-top: 6px;
  color: var(--fd-colors-system-info-content-accent);

  > div {
    margin-right: 4px;
  }
`;

export const AttentionContainer = styled.div`
  display: flex;
  width: 100%;
  margin-left: 2px;
  margin-top: 6px;
  color: var(--fd-colors-system-warning-content-accent);

  > div {
    margin-right: 4px;
  }
`;

export const WillPaysLabel = styled.div`
  color: var(--fd-colors-content-subtle);
  position: absolute;
  right: 18px;
  font-size: 12px;
`;
